import React from 'react';
import { Activity, Flame, AlertCircle, TrendingUp, Compass, Info, ShieldCheck } from 'lucide-react';
import InfoTooltip from './InfoTooltip';

/**
 * MechanicalExposureCard (NousMetrics v2.5)
 * Monitoriza el estrés osteoarticular, picos de carga mecánica y desacopla el ACWR.
 * Compatible al 100% con Safari WebKit / iOS (sin mix-blend-mode).
 */
export default function MechanicalExposureCard({ exposure, className = '' }) {
  if (!exposure) return null;

  const score = exposure.score ?? exposure.exposureScore ?? 35;
  const level = exposure.level ?? exposure.exposureLevel ?? 'MODERATE';
  const drivers = exposure.drivers || [];
  const spikeIndex = exposure.loadSpikeIndex ?? exposure.spikeIndex ?? null;
  const acute7d = exposure.acute7d ?? exposure.acuteExposure7d ?? 0;
  const chronic28d = exposure.chronic28d ?? exposure.chronicExposure28d ?? 0;
  const acwrDescriptive = exposure.acwrDescriptive ?? exposure.acwr ?? null;

  const levelConfigs = {
    LOW: {
      color: '#10b981',
      bg: 'rgba(16, 185, 129, 0.12)',
      border: 'rgba(16, 185, 129, 0.3)',
      label: 'Exposición Baja',
      desc: 'Carga osteoarticular bien tolerada sin picos agudos'
    },
    MODERATE: {
      color: '#38bdf8',
      bg: 'rgba(56, 189, 248, 0.12)',
      border: 'rgba(56, 189, 248, 0.3)',
      label: 'Exposición Óptima',
      desc: 'Estímulo mecánico progresivo dentro del pasillo habitual'
    },
    ELEVATED: {
      color: '#f59e0b',
      bg: 'rgba(245, 158, 11, 0.12)',
      border: 'rgba(245, 158, 11, 0.3)',
      label: 'Exposición Elevada',
      desc: 'Pico de impacto o volumen reciente; vigilar tendones y gemelos'
    },
    HIGH_EXPOSURE: {
      color: '#ef4444',
      bg: 'rgba(239, 68, 68, 0.12)',
      border: 'rgba(239, 68, 68, 0.3)',
      label: 'Sobrecarga Mecánica',
      desc: 'Pico agudo de carga excéntrica; se recomienda priorizar descarga articular'
    }
  };

  const config = levelConfigs[level] || levelConfigs.MODERATE;

  return (
    <div 
      className={`glass-panel ${className}`} 
      style={{
        borderRadius: '16px',
        padding: '1.25rem',
        background: 'rgba(15, 23, 42, 0.75)',
        border: `1px solid ${config.border}`,
        boxSizing: 'border-box'
      }}
    >
      {/* Header Bento */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '10px',
            background: config.bg,
            border: `1px solid ${config.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Flame size={18} color={config.color} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.92rem', fontWeight: 700, color: '#f8fafc' }}>
                Exposición Mecánica
              </span>
              <InfoTooltip text="El Índice de Exposición Mecánica evalúa el impacto articular, volumen y descensos en running. Sustituye al ACWR directo por un modelo multifactorial preventivo." />
            </div>
            <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.6)' }}>
              Mechanical Exposure Engine 1.0
            </div>
          </div>
        </div>

        {/* Score Badge */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: config.bg,
          border: `1px solid ${config.border}`,
          borderRadius: '999px',
          padding: '4px 10px'
        }}>
          <span style={{ fontSize: '0.86rem', fontWeight: 800, color: config.color }}>
            {score}/100
          </span>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: config.color, textTransform: 'uppercase' }}>
            {config.label}
          </span>
        </div>
      </div>

      <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1rem', lineHeight: 1.4 }}>
        {config.desc}
      </div>

      {/* Métricas Principales */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem', marginBottom: '1rem' }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '10px',
          padding: '0.65rem 0.75rem'
        }}>
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>Carga Aguda 7d</div>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#f8fafc', marginTop: '2px' }}>
            {Math.round(acute7d)}
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '10px',
          padding: '0.65rem 0.75rem'
        }}>
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>Carga Crónica 28d</div>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#f8fafc', marginTop: '2px' }}>
            {Math.round(chronic28d)}
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '10px',
          padding: '0.65rem 0.75rem'
        }}>
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
            ACWR Descriptivo
          </div>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: acwrDescriptive && acwrDescriptive > 1.4 ? '#f59e0b' : '#38bdf8', marginTop: '2px' }}>
            {acwrDescriptive ? acwrDescriptive.toFixed(2) : '—'}
          </div>
        </div>
      </div>

      {/* Drivers de Carga Mecánica */}
      {drivers.length > 0 && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: '10px',
          padding: '0.75rem 0.85rem'
        }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '0.04em' }}>
            Factores Clave de Exposición
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {drivers.map((driver, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '0.76rem', color: 'rgba(255,255,255,0.85)' }}>
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: config.color, flexShrink: 0 }} />
                <span>{driver}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nota no clínica */}
      <div style={{ marginTop: '0.75rem', fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', lineHeight: 1.3 }}>
        Métrica descriptiva de progresión de carga osteoarticular. No constituye diagnóstico médico ni garantía lesiva.
      </div>
    </div>
  );
}
